// @flow

import * as React from 'react';
import Group from 'sail/Group';
import Box from 'sail/Box';
import Icon from 'sail/Icon';
import {ContentListItem} from 'sail/ContentList';
import LastChecked from './LastChecked';
import CheckerMessage from './CheckerMessage';
import IntegrationCheck, {IntegrationCheckSummary} from './IntegrationCheck';
import CopyableTestCardNumber from './CopyableTestCardNumber';
import type {IntegrationCheckersDataIntegrationCheckerScenarioResult} from '../../generatedTypes';

/**
 * Component used for each test scenario
 * @param {IntegrationCheckerResult} props.result
 * @param {Preact.Component} title
 * @param {Preact.Component} testCardNumber
 */
type Props = {
  result: ?IntegrationCheckersDataIntegrationCheckerScenarioResult,
  title: string | React.Node,
  testCardNumber: React.Element<typeof CopyableTestCardNumber>,
  dashboardUrl: string,
};
type State = {
  expanded: boolean,
};
class IntegrationCheckerScenario extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  toggleExpand() {
    this.setState((state) => ({
      expanded: !state.expanded,
    }));
    if (
      window.siteAnalytics &&
      window.siteAnalytics.trackIntegrationCheckerAction
    ) {
      window.siteAnalytics.trackIntegrationCheckerAction('expand_scenario', {
        scenario: this.props.title,
      });
    }
  }

  render() {
    const {result, title, testCardNumber, dashboardUrl} = this.props;

    if (!result) {
      return null;
    }

    return (
      <Box
        style={{paddingBottom: '1px'}}
        divider={{color: 'light', width: 1, side: 'bottom'}}
      >
        <ContentListItem
          style={{width: '100%'}}
          background="offset"
          divider={false}
          start={
            <Box onClick={() => this.toggleExpand()}>
              <Icon
                className={
                  this.state.expanded
                    ? 'integration-checkers-chevron integration-checkers-chevron-down'
                    : 'integration-checkers-chevron'
                }
                icon="chevronRight"
                color="gray"
                size={14}
              />
            </Box>
          }
          title={
            <Box
              style={{
                cursor: 'pointer',
              }}
              onClick={() => this.toggleExpand()}
            >
              {title}
            </Box>
          }
          description={
            <Box className="integration-checker-transition">
              <Group
                className={
                  this.state.expanded
                    ? 'integration-checkers-testcases integration-checkers-testcases-hidden'
                    : 'integration-checkers-testcases integration-checkers-testcases-show'
                }
              >
                {this.state.expanded ? null : (
                  <IntegrationCheckSummary
                    successCount={result.checks.reduce(
                      (acc, curr) => (curr && curr.success ? acc + 1 : acc),
                      0,
                    )}
                    incompleteCount={result.checks.reduce(
                      (acc, curr) =>
                        curr &&
                        // Check for incomplete when no success key is returned OR success = false
                        (!curr.hasOwnProperty('success') ||
                          curr.success === null)
                          ? acc + 1
                          : acc,
                      0,
                    )}
                    totalCount={result.checks.length}
                  />
                )}
              </Group>
            </Box>
          }
          end={testCardNumber}
        />
        <Box background="offset" padding={{left: 48, right: 20}}>
          <Box className="integration-checker-transition">
            <Box
              className={
                this.state.expanded
                  ? 'integration-checkers-testcases integration-checkers-testcases-show'
                  : 'integration-checkers-testcases integration-checkers-testcases-hidden'
              }
            >
              {this.state.expanded ? (
                <Box padding={{vertical: 12}}>
                  <Group spacing={8}>
                    {result.checks.map((testCase, index) => {
                      return (
                        <IntegrationCheck
                          key={index}
                          title={
                            <CheckerMessage name={testCase && testCase.name} />
                          }
                          errorMessageCode={
                            testCase &&
                            testCase.errors[0] &&
                            testCase.errors[0].message
                          }
                          errorMessage={
                            testCase &&
                            testCase.errors[0] &&
                            testCase.errors[0].message_string
                          }
                          success={testCase && testCase.success}
                          dashboardUrl={dashboardUrl}
                          event={testCase && testCase.event}
                          log={testCase && testCase.log}
                        />
                      );
                    })}
                    <LastChecked result={result} dashboardUrl={dashboardUrl} />
                  </Group>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default IntegrationCheckerScenario;
